import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import LegalLayout from "../components/legal/LegalLayout";

const LAST_UPDATED = "November 19, 2025";

export default function Terms() {
    useEffect(() => {
        document.title = "Terms & Conditions — BetweenUs";
    }, []);
    return (
        <LegalLayout
            eyebrow="Legal"
            title="Terms & Conditions"
            updatedAt={LAST_UPDATED}
            siblingLabel="Privacy Policy"
            siblingTo="/privacy"
            testIdRoot="terms"
        >
            <section data-testid="terms-intro">
                <h2>1. Introduction</h2>
                <p>
                    These Terms and Conditions (“Terms”) govern your access to
                    and use of the BetweenUs mobile application and related
                    services (“BetweenUs”, “the App”), provided by Tylegn
                    Gaming and Entertainment, LLC (“Tylegn”, “we”, “us”,
                    “our”). By creating an account, installing, or using
                    BetweenUs, you agree to be bound by these Terms. If you
                    do not agree, do not use the App.
                </p>
                <p>
                    BetweenUs is a communication and guidance tool designed
                    to help two or more people better understand each
                    other’s perspectives and co‑create agreements. It is{" "}
                    <strong>
                        not therapy, crisis counseling, legal advice, or a
                        substitute for professional services.
                    </strong>
                </p>
                <p>
                    You should print or save a copy of these Terms for your
                    records.
                </p>
            </section>

            <section>
                <h2>2. Eligibility and Accounts</h2>
                <ul>
                    <li>You must be at least 18 years old to create an account.</li>
                    <li>
                        Parents or legal guardians may use the App in
                        structured conversations with their teen, in
                        compliance with applicable law in their region. You
                        are responsible for obtaining any necessary consent
                        and for supervising your teen’s use of the App.
                    </li>
                    <li>
                        You are responsible for maintaining the
                        confidentiality of your login credentials and for
                        all activities that occur under your account.
                    </li>
                    <li>
                        You agree to provide accurate, current information
                        and to keep it updated.
                    </li>
                    <li>
                        Tylegn may suspend or terminate your account at any
                        time if we reasonably believe you have violated
                        these Terms or used the App in a harmful or unlawful
                        way.
                    </li>
                </ul>
            </section>

            <section>
                <h2>3. License to Use BetweenUs</h2>
                <p>
                    Subject to these Terms, Tylegn grants you a limited,
                    non‑exclusive, non‑transferable, revocable license to
                    download and use BetweenUs on your personal devices for
                    your own non‑commercial use. You may not sublicense,
                    resell, or exploit the App for commercial purposes
                    without our prior written consent.
                </p>
            </section>

            <section>
                <h2>4. Acceptable Use</h2>
                <p>You agree that you will not use BetweenUs to:</p>
                <ul>
                    <li>Harass, threaten, abuse, or intimidate any person.</li>
                    <li>Encourage violence, self‑harm, or harm to others.</li>
                    <li>
                        Share content that is hateful, discriminatory,
                        exploitative, or illegal.
                    </li>
                    <li>
                        Share another person’s personal or confidential
                        information without their consent.
                    </li>
                    <li>Impersonate any person or misrepresent your identity.</li>
                    <li>
                        Attempt to reverse engineer, decompile, or modify
                        the App or its code.
                    </li>
                    <li>
                        Interfere with the security, integrity, or
                        performance of the App.
                    </li>
                </ul>
                <p>
                    Tylegn may remove content, suspend features, or
                    terminate accounts where we reasonably believe there has
                    been a breach of acceptable use.
                </p>
            </section>

            <section>
                <h2>5. AI‑Generated Content and Limitations</h2>
                <p>
                    BetweenUs uses artificial intelligence to help summarize
                    perspectives, suggest language, and generate possible
                    options for agreements. By using the App, you understand
                    and agree that:
                </p>
                <ul>
                    <li>
                        AI‑generated content may be inaccurate, incomplete,
                        or inappropriate in some contexts.
                    </li>
                    <li>
                        The App does not provide therapy, crisis
                        intervention, legal advice, or medical advice.
                    </li>
                    <li>
                        You remain fully responsible for your own decisions,
                        actions, and communications.
                    </li>
                    <li>
                        You should not rely on the App in emergencies or
                        life‑threatening situations.
                    </li>
                </ul>
                <p>
                    If you or someone else is in immediate danger, you must
                    contact local emergency services or a qualified
                    professional and not rely on the App.
                </p>
            </section>

            <section>
                <h2>6. User Content</h2>
                <p>
                    “User Content” means any text, audio, voice notes,
                    images, or other materials you submit, upload, or record
                    in the App.
                </p>
                <p>
                    You retain ownership of your User Content. By submitting
                    User Content, you grant Tylegn a worldwide,
                    non‑exclusive, royalty‑free license to host, store,
                    process, and display that content solely for the purpose
                    of operating, improving, and providing the App and
                    related services.
                </p>
                <p>You represent and warrant that:</p>
                <ul>
                    <li>You have the right to submit the User Content.</li>
                    <li>
                        The User Content does not violate any third‑party
                        rights or applicable law.
                    </li>
                </ul>
                <p>
                    We may remove or restrict access to User Content if we
                    reasonably believe it violates these Terms or is
                    harmful.
                </p>
            </section>

            <section>
                <h2>7. Safety, Crisis, and Sensitive Situations</h2>
                <p>
                    BetweenUs is not designed or licensed as a crisis or
                    emergency service. We may include features that attempt
                    to detect certain high‑risk phrases or topics (such as
                    self‑harm, suicide, abuse, or violence) and display
                    safety notices or resource links, but these features are
                    not comprehensive and do not replace professional
                    support.
                </p>
                <p>You agree not to use the App:</p>
                <ul>
                    <li>As a replacement for emergency services.</li>
                    <li>
                        To manage situations involving imminent risk of
                        harm.
                    </li>
                </ul>
                <p>
                    If you or another person may be at risk, you must
                    immediately seek help from emergency services, a crisis
                    hotline, or a qualified professional.
                </p>
            </section>

            <section>
                <h2>8. Payments and Subscriptions (if applicable)</h2>
                <p>
                    If BetweenUs offers paid features, subscriptions, or
                    in‑app purchases:
                </p>
                <ul>
                    <li>
                        Pricing, billing interval, and included features
                        will be shown at the point of purchase.
                    </li>
                    <li>
                        Payments will typically be processed through the
                        relevant app store (Apple App Store, Google Play, or
                        similar) under their own terms and billing systems.
                    </li>
                    <li>
                        Subscriptions will auto‑renew unless you cancel at
                        least 24 hours before the end of the current billing
                        period, according to your app store settings.
                    </li>
                    <li>
                        Tylegn does not control and is not responsible for
                        app store billing systems; any billing disputes
                        should be raised with the store where you purchased.
                    </li>
                    <li>
                        We may change pricing or introduce new plans in the
                        future; if we do, we will provide notice and give
                        you an opportunity to cancel before changes take
                        effect.
                    </li>
                </ul>
            </section>

            <section>
                <h2>9. Intellectual Property</h2>
                <p>
                    All rights, title, and interest in and to BetweenUs
                    (excluding User Content), including software, design,
                    text, graphics, logos, trademarks, and other content,
                    are owned by Tylegn or our licensors and are protected
                    by intellectual property laws.
                </p>
                <p>You may not:</p>
                <ul>
                    <li>
                        Copy, modify, or create derivative works from the
                        App.
                    </li>
                    <li>
                        Use our trademarks, trade dress, or branding without
                        our written permission.
                    </li>
                    <li>
                        Remove or alter any copyright, trademark, or
                        proprietary notices in the App.
                    </li>
                </ul>
            </section>

            <section>
                <h2>10. Third‑Party Services and Links</h2>
                <p>
                    BetweenUs may link to or integrate with third‑party
                    services, such as authentication providers, analytics,
                    storage, or AI platforms. These services are governed
                    by their own terms and privacy policies, and Tylegn is
                    not responsible for their content, policies, or
                    practices.
                </p>
            </section>

            <section>
                <h2>11. Termination</h2>
                <p>
                    We may suspend or terminate your access to the App at
                    any time, with or without notice, if:
                </p>
                <ul>
                    <li>You violate these Terms,</li>
                    <li>We are required to do so by law,</li>
                    <li>We discontinue the App.</li>
                </ul>
                <p>
                    You may stop using the App at any time and, where
                    applicable, delete your account or request account
                    deletion. Termination does not affect any rights or
                    obligations that, by their nature, should continue (for
                    example, disclaimers, limitations of liability).
                </p>
            </section>

            <section>
                <h2>12. Disclaimers</h2>
                <p>
                    The App and all content are provided on an “AS IS” and
                    “AS AVAILABLE” basis without warranties of any kind,
                    whether express or implied, including but not limited
                    to implied warranties of merchantability, fitness for a
                    particular purpose, or non‑infringement.
                </p>
                <p>We do not warrant that:</p>
                <ul>
                    <li>
                        The App will be uninterrupted, secure, or
                        error‑free,
                    </li>
                    <li>
                        Any advice or content obtained through the App will
                        be accurate or reliable,
                    </li>
                    <li>
                        Conflicts will be resolved or relationships will
                        improve.
                    </li>
                </ul>
                <p>You use the App at your own risk.</p>
            </section>

            <section>
                <h2>13. Limitation of Liability</h2>
                <p>
                    To the fullest extent permitted by law, Tylegn and our
                    officers, directors, employees, contractors, and
                    agents will not be liable for any indirect, incidental,
                    special, consequential, or punitive damages, or any
                    loss of profits or revenues, arising out of or related
                    to your use of the App.
                </p>
                <p>
                    To the extent we are found liable, our total liability
                    for any claims arising out of or related to the App
                    will not exceed the greater of (a) the amount you paid
                    us in the 3 months prior to the event giving rise to
                    the claim, or (b) USD $50.
                </p>
                <p>
                    Some jurisdictions do not allow certain disclaimers or
                    limitations, so parts of this section may not apply to
                    you.
                </p>
            </section>

            <section>
                <h2>14. Governing Law, Disputes & Arbitration</h2>

                <h3>Informal resolution first</h3>
                <p>
                    Before filing any formal claim, you agree to first
                    contact us at{" "}
                    <a href="mailto:support@tylegn.com">
                        support@tylegn.com
                    </a>{" "}
                    and describe the issue in reasonable detail. We’ll
                    work in good faith to resolve it. Most concerns can be
                    resolved within 60 days of that first contact.
                </p>

                <h3>Governing law</h3>
                <p>
                    These Terms are governed by the laws of the State of
                    Idaho, United States, where Tylegn Gaming and
                    Entertainment, LLC is organized, excluding its
                    conflict‑of‑laws rules. <strong>Exception:</strong>{" "}
                    if you are a consumer located outside the United
                    States, the mandatory consumer‑protection laws of
                    your country of residence will continue to apply to
                    you where they are required and cannot be waived by
                    contract.
                </p>

                <h3>Binding individual arbitration</h3>
                <p>
                    Any dispute, claim, or controversy arising out of or
                    relating to these Terms or the App that is not resolved
                    through informal resolution will be resolved by{" "}
                    <strong>binding individual arbitration</strong>{" "}
                    administered by the American Arbitration Association
                    (“AAA”) under its Consumer Arbitration Rules, or by a
                    comparable arbitral institution selected by mutual
                    agreement. The arbitration will be conducted in
                    English and, where permitted by applicable law,{" "}
                    <strong>held remotely by default</strong> so that
                    users around the world are not required to travel. If
                    an in‑person hearing is required, the seat of
                    arbitration will be a location in the United States
                    reasonably convenient to both parties, taking into
                    account the user’s place of residence; if mandatory
                    law in your jurisdiction requires a different seat,
                    that law will control. Judgment on the arbitrator’s
                    award may be entered in any court of competent
                    jurisdiction.
                </p>

                <h3>Class‑action waiver</h3>
                <p>
                    You and Tylegn agree that any dispute will be brought
                    on an <strong>individual basis only</strong>, and not
                    as part of a class, collective, consolidated, or
                    representative action. The arbitrator may not
                    consolidate more than one person’s claims or preside
                    over any form of representative proceeding. If this
                    class‑action waiver is held unenforceable in a
                    particular case, that case will proceed in court
                    rather than arbitration — the remaining provisions of
                    this Section will still apply to all other disputes.
                </p>

                <h3>Exceptions to arbitration</h3>
                <ul>
                    <li>
                        Either party may bring an individual action in a
                        small‑claims court (or equivalent) instead of
                        arbitration, so long as the action remains on an
                        individual basis.
                    </li>
                    <li>
                        Either party may seek injunctive or equitable
                        relief in court to protect intellectual property
                        rights, trade secrets, or confidential
                        information.
                    </li>
                    <li>
                        Nothing in this Section prevents you from reporting
                        concerns to a government agency or seeking relief
                        that, by law, cannot be waived.
                    </li>
                </ul>

                <h3>30‑day opt‑out</h3>
                <p>
                    You may opt out of this arbitration and class‑action
                    waiver by sending written notice within{" "}
                    <strong>30 days</strong> of first accepting these
                    Terms to{" "}
                    <a href="mailto:support@tylegn.com">
                        support@tylegn.com
                    </a>{" "}
                    (or by mail to the address in Section 16), stating
                    your name, the email used with the App, and that you
                    are opting out of arbitration. Opting out will not
                    affect the remaining provisions of these Terms.
                </p>

                <h3>Local consumer rights</h3>
                <p>
                    Nothing in this Section limits any non‑waivable rights
                    you may have as a consumer under the laws of your
                    country or region of residence, including the right to
                    bring certain claims in your local courts where that
                    right cannot be waived by contract.
                </p>
            </section>

            <section>
                <h2>15. Changes to These Terms</h2>
                <p>
                    We may update these Terms from time to time. When we
                    do, we will change the “Last updated” date at the top,
                    and may provide additional notice (such as an in‑app
                    notification).
                </p>
                <p>
                    Your continued use of the App after changes take effect
                    constitutes your acceptance of the updated Terms.
                </p>
            </section>

            <section>
                <h2>16. Contact Us</h2>
                <p>
                    If you have questions about these Terms, contact:
                </p>
                <address>
                    <strong>Tylegn Gaming and Entertainment, LLC</strong>
                    <br />
                    Email:{" "}
                    <a href="mailto:support@tylegn.com">
                        support@tylegn.com
                    </a>
                    <br />
                    Mailing Address: PO Box 3118, Hailey, ID 83333
                </address>
            </section>

            <p className="legal-cross-link">
                See also:{" "}
                <Link to="/privacy" data-testid="terms-link-privacy">
                    Privacy Policy
                </Link>
                {" "}·{" "}
                <Link to="/eula" data-testid="terms-link-eula">
                    EULA
                </Link>
                .
            </p>
        </LegalLayout>
    );
}
